import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  PamGrid,
  GridToolbar,
  GridConfigMenu,
  useChangeTracking,
  useGridConfigs,
  useNotification,
  notifyPermissionError,
} from "@1823-partners/core";
import * as XLSX from "xlsx";
import { Box, Paper, Stack, Typography } from "@mui/material";
import api, {
  getMyPermissions,
  getRelValAssumptions,
  getRelValDates,
  getRelValData,
  getRelValSofrCurve,
  getRelValUstCurve,
  saveAssetMenuData,
  saveRelValSourceData,
  saveRelValData,
} from "../api";
import SourceFileUploadButton from "./SourceFileUploadButton";
import {
  groupAssetMenuCurves,
  parseAssetMenuWorkbook,
} from "../utils/assetMenuImport";
import { parseRelValSourceWorkbook } from "../utils/sourceWorkbookImport";
import { nextMonday } from "../utils/weeklyDates";
import {
  buildRelValOverrideRows,
  buildRelValSnapshotRows,
} from "../utils/relvalSnapshot";

const formatAssumption = ({ value, format }) => {
  if (value == null) return "—";
  const n = Number(value);
  if (format === "percent") return `${(n * 100).toFixed(2)}%`;
  if (format === "bps") return `${n.toFixed(0)} bps`;
  return n.toLocaleString();
};

// Round-trip through Date so a well-shaped but impossible day (2026-02-31)
// is rejected rather than normalized into the following month.
const isRealIsoDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
};

/**
 * The snapshot's as-of date: the newest date any imported row carries.
 *
 * This used to be the date the Internal Spreads rows agreed on, which trusted
 * a single analyst-maintained column with nothing to cross-check it against.
 * The 2026-09-01 workbook shipped with its '1823 Input' sheet still dated
 * 08-10 while every market row read 08-31, so the entire snapshot filed three
 * weeks early — unreachable at its own week, and superseding the real 08-10
 * upload. The old guard only checked that those rows agreed with each other,
 * which they did.
 *
 * Taking the max instead tracks the data that actually moves, and reproduces
 * the workbook's own filename date on every menu from 08-03 onward. Curve
 * rows are deliberately not considered — they are not part of the grid, and
 * the 08-24 menu prints its curve at 08-26, which would date that snapshot
 * into the future.
 *
 * ISO dates sort correctly as strings, so no parsing is needed beyond
 * validating the shape. Returns null when no row carries a usable date.
 */
const getImportedAsOfDate = (rows) => {
  let latest = null;
  for (const row of rows) {
    const value = row.LAST_UPDATED_DATE;
    // A blank date is a row the workbook did not stamp, not an error.
    if (value == null || value === "") continue;
    if (!isRealIsoDate(value)) return null;
    if (latest === null || value > latest) latest = value;
  }
  return latest;
};

// One line for the staging notification. A single workbook keeps the
// original wording; a batch names every source so the user can confirm the
// whole week landed, and spells out per-source dates when the workbooks
// disagree (e.g. a weekly CLO Factbook alongside a daily corporate file).
const describeStagedUploads = (uploads) => {
  if (uploads.length === 1) {
    const [upload] = uploads;
    return `Staged ${upload.sourceType} source data for ${upload.asofdt}`;
  }
  const types = uploads.map((upload) => upload.sourceType).join(", ");
  const dates = new Set(uploads.map((upload) => upload.asofdt));
  if (dates.size === 1) {
    return `Staged ${uploads.length} source workbooks (${types}) for ${uploads[0].asofdt}`;
  }
  const detail = uploads
    .map((upload) => `${upload.sourceType} ${upload.asofdt}`)
    .join(", ");
  return `Staged ${uploads.length} source workbooks: ${detail}`;
};

// Files that failed to parse are named alongside the staged ones in a single
// notification: a batch is a whole week of workbooks, and losing the two that
// parsed because the third is malformed is worse than a partial import.
const describeFailedFiles = (failures) =>
  failures.map(({ fileName, message }) => `${fileName}: ${message}`).join("; ");

// One strip per base-rate curve. UST comes from USD_CMT; SOFR comes from the
// Asset Menu's uploaded SOFR Curve block. Both drive IMPLIED_YIELD, so both
// are shown — a blank SOFR strip is the visible signal that SOFR-linked rows
// are falling back to the workbook's frozen yield.
// Reshape a staged curve upload into the {tenor, yield} strip shape the
// endpoints return, so a pending import previews through the same component.
const curvePointsFor = (curveUploads, sourceType) =>
  (curveUploads.find((upload) => upload.source_type === sourceType)?.rows || []).map(
    (row) => ({ tenor: row.IDENTIFIER, yield: row.VALUE }),
  );

const CurvePanel = ({ label, points, emptyMessage, subLabel }) => {
  // A hidden strip is not a signal. When the curve is missing, say so and say
  // what it costs — SOFR-linked rows silently keep the workbook's frozen
  // yield, and there is nothing else on screen that shows it.
  if (!points?.length && !emptyMessage) return null;
  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1, mb: 1 }}>
      <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
        <Typography variant="overline" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {subLabel && (
          <Typography variant="caption" color="text.secondary">
            {subLabel}
          </Typography>
        )}
        {!points?.length && (
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        )}
        {(points || []).map((p) => (
          <Box key={p.tenor} sx={{ display: "flex", gap: 0.75 }}>
            <Typography variant="body2" color="text.secondary">
              {p.tenor}:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {p.yield == null ? "—" : `${Number(p.yield).toFixed(3)}%`}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

const AssumptionsPanel = ({ assumptions }) => {
  if (!assumptions?.length) return null;
  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1, mb: 1 }}>
      <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
        <Typography variant="overline" sx={{ fontWeight: 600 }}>
          Assumptions
        </Typography>
        {assumptions.map((a) => (
          <Box key={a.key} sx={{ display: "flex", gap: 0.75 }}>
            <Typography variant="body2" color="text.secondary">
              {a.label}:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatAssumption(a)}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

const RelValGrid = () => {
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [hasPendingImport, setHasPendingImport] = useState(false);
  const [pendingImportDate, setPendingImportDate] = useState(null);
  const [pendingSourceImports, setPendingSourceImports] = useState({});
  const [quickFilterText, setQuickFilterText] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [assumptions, setAssumptions] = useState([]);
  const [ustCurve, setUstCurve] = useState([]);
  const [sofrCurve, setSofrCurve] = useState([]);
  const [sofrCurveFailed, setSofrCurveFailed] = useState(false);
  // The curve's own date. Observations resolve as "latest on or before", so
  // this is not necessarily the selected week — showing it is what keeps a
  // stale curve from reading as current.
  const [sofrCurveAsOf, setSofrCurveAsOf] = useState(null);
  // Curve observations parsed out of a staged Asset Menu, saved alongside the
  // snapshot. Held separately because they are source observations, not rows.
  const [pendingCurveImports, setPendingCurveImports] = useState([]);
  // UI hint only — the server enforces 'relval.data:write' on /save-relval.
  // Gates the Save button and CURRENT_SPREAD cell editing.
  const [canWrite, setCanWrite] = useState(false);
  const gridRef = useRef(null);
  const dataRequestIdRef = useRef(0);
  const { showNotification } = useNotification();

  useEffect(() => {
    getMyPermissions()
      .then((res) => {
        const perms = res.data?.permissions || [];
        setCanWrite(perms.includes("relval.data:write"));
      })
      .catch(() => {
        setCanWrite(false);
      });
  }, []);

  const getRowId = useCallback((data) => {
    return data.IDENTIFIER || data._tempId;
  }, []);

  const isNewRow = useCallback((data) => {
    return data._tempId != null && data.IDENTIFIER == null;
  }, []);

  const { updatedRows, handleCellValueChanged, clearChanges, getChangedData } =
    useChangeTracking({ getRowId, isNewRow });

  // Column definitions for the pivot grid. ASSET_CLASS and SUB_CLASS act as
  // row-group keys: hidden as standalone columns and surfaced by AG Grid as
  // auto-generated group columns (one per level) via
  // groupDisplayType="multipleColumns" on the PamGrid below. Header names
  // here drive those auto-column headers.
  const columnDefs = useMemo(
    () => [
      {
        field: "ASSET_CLASS",
        headerName: "Asset Class",
        rowGroup: true,
        hide: true,
      },
      {
        field: "SUB_CLASS",
        headerName: "Sub-Class",
        rowGroup: true,
        hide: true,
      },
      {
        field: "IDENTIFIER",
        headerName: "Identifier",
        editable: false,
        hide: true,
        pinned: "left",
      },
      {
        field: "SHORT_NAME",
        headerName: "Short Name",
        editable: false,
        pinned: "left",
      },
      // ───── Output sheet cols N, O & P ─────
      {
        field: "SOURCE",
        headerName: "Source",
        editable: false,
      },
      {
        // Last updated date for this row's source feed. May differ from the
        // selected date in the toolbar — e.g. BAML_CLO is weekly so its row
        // shows the most recent BAML_CLO publish date ≤ selected date.
        field: "LAST_UPDATED_DATE",
        headerName: "Date",
        editable: false,
      },
      {
        field: "RATING",
        headerName: "Rating",
        editable: false,
      },
      // ───── Output sheet col Q ─────
      {
        field: "WAL",
        headerName: "WAL",
        editable: false,
      },
      // ───── Output sheet col R ─────
      {
        field: "CURRENT_SPREAD",
        headerName: "Current Value",
        headerTooltip:
          "INDEX_OAS_TSY values are shown and edited in bps; other rows use native units.",
        editable: canWrite,
        type: "numericColumn",
        cellClassRules: {
          "relval-cell-edited": (params) => {
            if (!params.data) return false;
            const rowId = getRowId(params.data);
            return Boolean(updatedRows[rowId]?.CURRENT_SPREAD);
          },
        },
        // Backend stores spreads as Bloomberg-native percent (e.g. 0.781119).
        // We display bps to the user and divide their input back to percent on edit
        // so the row's stored CURRENT_SPREAD always matches the backend unit.
        valueParser: (params) => {
          const val = Number(params.newValue);
          if (isNaN(val)) return params.oldValue;
          return params.data?.FIELD === "INDEX_OAS_TSY" ? val / 100 : val;
        },
        valueFormatter: (params) => {
          if (params.value == null) return "";
          const isSpread = params.data?.FIELD === "INDEX_OAS_TSY";
          const display = isSpread
            ? Number(params.value) * 100
            : Number(params.value);
          return display.toFixed(2);
        },
      },
      // ───── Output sheet cols S–X (scaffolded; backend returns null until wired up) ─────
      {
        field: "ASSET_LEVERAGE",
        headerName: "Leverage",
        editable: false,
        type: "numericColumn",
        valueFormatter: (params) =>
          params.value == null ? "" : Number(params.value).toFixed(4),
      },
      {
        field: "FRICTION_COST_TO_TARGET",
        headerName: "Friction",
        editable: false,
        type: "numericColumn",
        valueFormatter: (params) =>
          params.value == null ? "" : Number(params.value).toFixed(2),
      },
      {
        field: "REMAINING_C1_PERMITTED",
        headerName: "Remaining C1",
        editable: false,
        type: "numericColumn",
        valueFormatter: (params) =>
          params.value == null
            ? ""
            : (Number(params.value) * 100).toFixed(2) + "%",
      },
      {
        field: "BASE_RATE",
        headerName: "Base Rate",
        editable: false,
      },
      {
        field: "IMPLIED_YIELD",
        headerName: "Implied Yield",
        editable: false,
        type: "numericColumn",
        valueFormatter: (params) =>
          params.value == null ? "" : Number(params.value).toFixed(4) + "%",
      },
      {
        field: "RBC",
        headerName: "RBC",
        editable: false,
        type: "numericColumn",
        valueFormatter: (params) =>
          params.value == null
            ? ""
            : (Number(params.value) * 100).toFixed(2) + "%",
      },
      {
        field: "SPREAD_RBC_CAPITAL",
        headerName: "Spread / RBC Capital",
        editable: false,
        type: "numericColumn",
      },
      {
        field: "SP_CAPITAL",
        headerName: "S&P",
        editable: false,
        type: "numericColumn",
        valueFormatter: (params) =>
          params.value == null
            ? ""
            : (Number(params.value) * 100).toFixed(2) + "%",
      },
      {
        field: "SPREAD_SP_CAPITAL",
        headerName: "Spread / S&P Capital",
        editable: false,
        type: "numericColumn",
      },
      // ───── Output sheet cols Y–AB (static metadata, wired) ─────
      {
        field: "FIXED_FLOATING",
        headerName: "Fixed / Floating",
        editable: false,
      },
      {
        // Output col Z. Workbook formula references col E only (collateral
        // WAL), but the displayed header in the workbook combines collateral
        // and structural concepts — most rows have E == F, so they read the
        // same; CRE CLO / SASB floating rows have E = "1.5 / 1 1 Yr. Ext."
        // strings while F = "Floating".
        field: "COLLATERAL_WAL_STRUCT",
        headerName: "Collateral WAL / Struct.",
        editable: false,
      },
      {
        field: "SPREAD_DUR_RANGE",
        headerName: "Spread Dur. Range",
        editable: false,
      },
      {
        // Sparse — only ~20 rows have workbook-hardcoded notes (CRE CLO BSL/MM,
        // CRE CLO Managed, SASB Industrial/Hotel, RTL tranches). Other rows
        // render blank; persisted user edits aren't wired yet.
        field: "BOND_STRUCT_NOTES",
        headerName: "Bond Struct. Notes",
        editable: false,
      },
      // ───── Output sheet cols AC–AF (history-driven, wired for BBG/BAML/BAML_CLO) ─────
      // Wells RMBS and Internal Spreads are single-snapshot sources with no
      // historical series in this repo — those rows render blank until a
      // history feed is ingested. WoW Δ is already in bps; backend computes
      // the percent→bps scaling for BBG INDEX_OAS_TSY rows before delivery.
      {
        field: "WOW_SPREAD_CHANGE",
        headerName: "WoW Δ Spread",
        editable: false,
        type: "numericColumn",
        valueFormatter: (params) => {
          if (params.value == null) return "";
          const v = Number(params.value);
          if (isNaN(v)) return "";
          const sign = v > 0 ? "+" : "";
          return `${sign}${v.toFixed(1)} bps`;
        },
      },
      {
        field: "SPREAD_HISTORY_MIN_DATE",
        headerName: "Spread History Min Date",
        editable: false,
      },
      {
        field: "RANGE_2025",
        headerName: "2025 Range",
        editable: false,
      },
      {
        field: "RANGE_3Y",
        headerName: "3 Year Range",
        editable: false,
      },
    ],
    [canWrite, getRowId, updatedRows],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    [],
  );

  // Applied to *each* auto-generated group column (one per group level under
  // groupDisplayType="multipleColumns"). Per-column header text comes from
  // the headerName on each rowGroup field def in columnDefs above.
  // Pinned left so Asset Class / Sub-Class stay anchored alongside Short Name
  // as the user scrolls horizontally. Identifier is hidden by default.
  const autoGroupColumnDef = useMemo(
    () => ({
      pinned: "left",
      cellRendererParams: { suppressCount: true },
    }),
    [],
  );

  // Load available dates on mount, auto-select most recent
  const loadDates = useCallback(
    async (preferredDate = null) => {
      try {
        const response = await getRelValDates();
        // RelVal runs weekly, so the picker deals in weeks: every stored
        // publication date collapses onto the Monday of the week it belongs
        // to. Stored as-of dates are untouched — this is purely how the week
        // is addressed in the UI.
        const weeks = Array.from(
          new Set(
            (response.data || []).map((d) =>
              nextMonday(new Date(d).toISOString().slice(0, 10)),
            ),
          ),
        )
          .sort()
          .reverse();
        setAvailableDates(weeks);
        setSelectedDate(
          (currentDate) =>
            nextMonday(preferredDate) || currentDate || weeks[0] || "",
        );
      } catch (error) {
        console.error("Error loading relval dates:", error);
        notifyPermissionError(showNotification, error);
      }
    },
    [showNotification],
  );

  // Load data for the selected date (rows + UST curve snapshot, in parallel)
  const loadData = useCallback(
    async (asofdt) => {
      const requestId = dataRequestIdRef.current + 1;
      dataRequestIdRef.current = requestId;
      setIsLoading(true);
      try {
        const [dataResp, curveResp, sofrResp] = await Promise.all([
          getRelValData(asofdt),
          getRelValUstCurve(asofdt),
          // SOFR annotates the grid rather than filling it, and the endpoint
          // does not exist on a server that predates the backend change — the
          // frontend deploys independently. Never let it take the rows down.
          getRelValSofrCurve(asofdt).catch((error) => {
            console.error("Error loading SOFR curve:", error);
            // Degrade, but not silently for a permission problem: that is
            // actionable (the deep-link Request-access button lives in this
            // helper) and looks nothing like the 404 this catch exists for.
            notifyPermissionError(showNotification, error);
            return null;
          }),
        ]);
        if (requestId !== dataRequestIdRef.current) return;
        setRowData(dataResp.data || []);
        setHasPendingImport(false);
        setPendingImportDate(null);
        setPendingCurveImports([]);
        setUstCurve(curveResp.data || []);
        // null is a failed read, [] is a week with no curve uploaded. They
        // read the same on screen otherwise, and they are not the same thing.
        setSofrCurve(sofrResp?.data?.points || []);
        setSofrCurveAsOf(sofrResp?.data?.asofdt || null);
        setSofrCurveFailed(sofrResp == null);
        clearChanges();
      } catch (error) {
        if (requestId !== dataRequestIdRef.current) return;
        console.error("Error loading relval data:", error);
        if (!notifyPermissionError(showNotification, error)) {
          showNotification("Failed to load data", "error");
        }
      } finally {
        if (requestId === dataRequestIdRef.current) setIsLoading(false);
      }
    },
    [clearChanges, showNotification],
  );

  // Load dates on mount
  useEffect(() => {
    loadDates();
  }, [loadDates]);

  // Load assumptions once on mount — they're hard-coded server-side.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getRelValAssumptions();
        if (!cancelled) setAssumptions(response.data || []);
      } catch (error) {
        console.error("Error loading relval assumptions:", error);
        if (!cancelled) {
          notifyPermissionError(showNotification, error);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showNotification]);

  // Load data when selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      loadData(selectedDate);
    }
  }, [selectedDate, loadData]);

  // Column sizing: autosize each column to max(header text + menu icon,
  // longest cell content). We deliberately do NOT call sizeColumnsToFit —
  // with the extra columns (Source, Rating, …) the total width can exceed
  // the viewport and AG Grid will show a horizontal scrollbar rather than
  // squeezing columns and truncating data.
  //
  // PamGrid already runs autoSizeAllColumns on grid ready (setTimeout 100)
  // and on firstDataRendered, so initial load is covered. We only need to
  // re-autosize on subsequent rowData swaps (date changes), since
  // firstDataRendered fires once per grid lifecycle.
  const handleGridReady = useCallback((params) => {
    const api = params.api;
    api.addEventListener("rowDataUpdated", () => api.autoSizeAllColumns());
  }, []);

  // Per-user grid layout persistence. We own the hook here (rather than
  // letting GridConfigMenu instantiate it internally) so we can bridge
  // gridConfigs.onGridReady into PamGrid's onGridReady — without that
  // bridge, a slow grid that mounts after the configs response would miss
  // the default-layout auto-apply.
  const gridConfigs = useGridConfigs(api, {
    app: "relval",
    gridId: "relative-value",
    gridApiRef: gridRef,
  });

  // Compose the local autosize-on-rowDataUpdated wiring with the config
  // hook's grid-ready bridge so neither clobbers the other.
  const handleGridReadyComposed = useCallback(
    (params) => {
      handleGridReady(params);
      gridConfigs.onGridReady(params);
    },
    [handleGridReady, gridConfigs],
  );

  // Save handler
  const handleSave = useCallback(async () => {
    const sourceUploads = Object.values(pendingSourceImports);
    if (sourceUploads.length) {
      if (hasPendingImport || getChangedData(rowData).length) {
        showNotification(
          "Reload the grid changes before saving staged source workbooks",
          "error",
        );
        return;
      }
      setIsSaving(true);
      try {
        for (const upload of sourceUploads) {
          await saveRelValSourceData({
            source_type: upload.sourceType,
            asofdt: upload.asofdt,
            file_name: upload.fileName,
            rows: upload.rows,
          });
          setPendingSourceImports((current) => {
            const remaining = { ...current };
            delete remaining[upload.sourceType];
            return remaining;
          });
        }
        const latestDate = sourceUploads
          .map((upload) => upload.asofdt)
          .filter(Boolean)
          .sort()
          .at(-1);
        showNotification(
          `Saved ${sourceUploads.length} RelVal source workbook${sourceUploads.length === 1 ? "" : "s"}`,
          "success",
        );
        // Reload the week, not the publication date. The selection is
        // already this Monday from staging, so the [selectedDate] effect
        // will not fire and this explicit read is the one that lands —
        // passing the raw date would show Friday's rows and UST curve
        // under a Monday label.
        const savedWeek = nextMonday(latestDate);
        await loadDates(latestDate);
        await loadData(savedWeek);
      } catch (error) {
        console.error("Error saving RelVal source data:", error);
        if (!notifyPermissionError(showNotification, error)) {
          showNotification(
            error?.response?.data?.error || "Failed to save source workbook",
            "error",
          );
        }
      } finally {
        setIsSaving(false);
      }
      return;
    }

    const changedRows = hasPendingImport ? rowData : getChangedData(rowData);
    if (changedRows.length === 0) {
      showNotification(
        hasPendingImport
          ? "The imported workbook contains no data rows"
          : "No changes to save",
        hasPendingImport ? "error" : "info",
      );
      return;
    }

    const rowsToSave = hasPendingImport ? rowData : changedRows;

    const seenIdentifiers = new Set();
    const duplicateIdentifiers = new Set();
    rowsToSave.forEach((row) => {
      const identifier = String(row.IDENTIFIER || "").trim();
      if (identifier && seenIdentifiers.has(identifier)) {
        duplicateIdentifiers.add(identifier);
      }
      if (identifier) seenIdentifiers.add(identifier);
    });
    if (duplicateIdentifiers.size > 0) {
      showNotification(
        `Duplicate row identifiers: ${Array.from(duplicateIdentifiers).join(", ")}`,
        "error",
      );
      return;
    }

    const invalidRows = rowsToSave.filter((row) => {
      const spread = row.CURRENT_SPREAD;
      const invalidSpread = hasPendingImport
        ? spread != null && spread !== "" && !Number.isFinite(Number(spread))
        : spread == null || spread === "" || !Number.isFinite(Number(spread));
      return (
        !String(row.IDENTIFIER || "").trim() ||
        !String(row.SHORT_NAME || "").trim() ||
        invalidSpread
      );
    });
    if (invalidRows.length > 0) {
      const names = invalidRows
        .map((row, index) => row.SHORT_NAME || `row ${index + 1}`)
        .join(", ");
      showNotification(
        `${hasPendingImport ? "Invalid snapshot rows" : "Invalid user changes"}: ${names}`,
        "error",
      );
      return;
    }

    const importedAsOfDate = hasPendingImport
      ? getImportedAsOfDate(rowsToSave)
      : null;
    if (hasPendingImport && !importedAsOfDate) {
      showNotification(
        "Imported rows must carry at least one valid as-of date",
        "error",
      );
      return;
    }

    setIsSaving(true);
    try {
      const saveAsOfDate = hasPendingImport ? importedAsOfDate : selectedDate;
      const payload = hasPendingImport
        ? buildRelValSnapshotRows(rowsToSave)
        : buildRelValOverrideRows(rowsToSave);

      // Attribution comes from the verified SWA principal server-side —
      // no client-supplied user field.
      if (hasPendingImport) {
        // Snapshot before curves, deliberately. There is no cross-table
        // transaction here, so the question is which half-applied state is
        // safer. Snapshot-then-curve leaves the snapshot showing the
        // workbook's own frozen SOFR yields — degraded but self-consistent,
        // and exactly the behaviour that predates this change.
        // Curve-then-snapshot would publish a curve for a week whose snapshot
        // never landed, silently moving the *previous* snapshot's SOFR yields.
        // Both tables are append-only and versioned by UPLOAD_ID, so a retry
        // supersedes rather than duplicates.
        await saveAssetMenuData({
          rows: payload,
          asofdt: saveAsOfDate,
          snapshot_complete: true,
        });
        // Filed under the snapshot's as-of date, not the curve's own printed
        // date — see groupAssetMenuCurves.
        for (const curve of pendingCurveImports) {
          await saveRelValSourceData({
            source_type: curve.source_type,
            asofdt: saveAsOfDate,
            file_name: curve.observed_date
              ? `Rel Val Asset Menu (curve printed ${curve.observed_date})`
              : "Rel Val Asset Menu",
            rows: curve.rows,
          });
        }
      } else {
        await saveRelValData({ rows: payload, asofdt: saveAsOfDate });
      }
      setHasPendingImport(false);
      setPendingImportDate(null);
      setPendingCurveImports([]);
      showNotification(
        hasPendingImport
          ? `Saved Asset Menu snapshot (${payload.length} rows)`
          : `Saved ${payload.length} user change${payload.length === 1 ? "" : "s"}`,
        "success",
      );
      if (hasPendingImport && saveAsOfDate) {
        // Same here: select and read the same week, or the grid and the
        // picker disagree.
        const savedWeek = nextMonday(saveAsOfDate);
        setSelectedDate(savedWeek);
        await loadDates(saveAsOfDate);
        await loadData(savedWeek);
      } else {
        await loadData(selectedDate);
      }
    } catch (error) {
      console.error("Error saving relval data:", error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification("Failed to save data", "error");
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    getChangedData,
    hasPendingImport,
    pendingCurveImports,
    pendingSourceImports,
    rowData,
    selectedDate,
    loadDates,
    loadData,
    showNotification,
  ]);

  // Accepts a single file or an array of files, so a whole week of source
  // workbooks can be staged in one gesture. Every file is parsed before any
  // of them is staged: a batch that mixes an Asset Menu workbook with source
  // workbooks, or that carries two workbooks for the same source, is
  // rejected whole rather than half-applied.
  const handleImport = useCallback(
    async (input) => {
      const files = (Array.isArray(input) ? input : [input]).filter(Boolean);
      if (!files.length) return;
      setIsImporting(true);
      try {
        const sourceUploads = [];
        const unrecognizedFiles = [];
        const failedFiles = [];
        for (const file of files) {
          let sourceUpload = null;
          try {
            sourceUpload = await parseRelValSourceWorkbook(file);
          } catch (sourceError) {
            if (
              String(sourceError?.message).startsWith("Unsupported workbook.")
            ) {
              unrecognizedFiles.push(file);
            } else {
              // A workbook of a known shape that would not parse. Record it
              // and keep going so the rest of the batch still stages.
              failedFiles.push({
                fileName: file?.name || "workbook",
                message: sourceError?.message || "Failed to parse workbook",
              });
            }
            continue;
          }
          if (sourceUpload) sourceUploads.push(sourceUpload);
          else unrecognizedFiles.push(file);
        }

        if (sourceUploads.length) {
          if (unrecognizedFiles.length) {
            throw new Error(
              "Import the Asset Menu workbook on its own — only source workbooks can be imported together",
            );
          }
          if (hasPendingImport || getChangedData(rowData).length) {
            throw new Error(
              "Save or reload the current grid changes before importing source workbooks",
            );
          }
          const seenSources = new Set();
          const duplicateSources = new Set();
          for (const upload of sourceUploads) {
            if (seenSources.has(upload.sourceType)) {
              duplicateSources.add(upload.sourceType);
            }
            seenSources.add(upload.sourceType);
          }
          if (duplicateSources.size) {
            throw new Error(
              `Two workbooks map to the same source (${Array.from(duplicateSources).join(", ")}) — import one file per source`,
            );
          }
          setPendingSourceImports((current) => {
            const next = { ...current };
            for (const upload of sourceUploads) {
              next[upload.sourceType] = upload;
            }
            return next;
          });
          if (failedFiles.length) {
            showNotification(
              `${describeStagedUploads(sourceUploads)}. ${failedFiles.length} file${
                failedFiles.length === 1 ? "" : "s"
              } failed — ${describeFailedFiles(failedFiles)}`,
              "warning",
            );
          } else {
            showNotification(describeStagedUploads(sourceUploads), "success");
          }
          // Follow the workbooks to their week. The staged weeks are folded
          // into the toolbar's option list (see toolbarDates), so a workbook
          // for a week the backend has never seen is selectable straight
          // away instead of leaving the picker blank.
          const stagedDate = sourceUploads
            .map((upload) => upload.asofdt)
            .filter(Boolean)
            .sort()
            .at(-1);
          if (stagedDate) setSelectedDate(nextMonday(stagedDate));
          return;
        }

        if (failedFiles.length && !unrecognizedFiles.length) {
          throw new Error(describeFailedFiles(failedFiles));
        }
        if (files.length > 1) {
          throw new Error(
            "Import the Asset Menu workbook on its own — only source workbooks can be imported together",
          );
        }
        if (Object.keys(pendingSourceImports).length) {
          throw new Error(
            "Save the staged source workbooks before importing an Asset Menu",
          );
        }
        const { rows: importedRows, curves } =
          await parseAssetMenuWorkbook(files[0]);
        const curveUploads = groupAssetMenuCurves(curves);
        dataRequestIdRef.current += 1;
        setIsLoading(false);
        setRowData(importedRows);
        setHasPendingImport(true);
        const importedDate = getImportedAsOfDate(importedRows);
        setPendingImportDate(nextMonday(importedDate));
        setPendingCurveImports(curveUploads);
        // Preview the staged curves rather than blanking both strips: the
        // whole point of the SOFR strip is to show what the pending snapshot
        // will compute its SOFR-linked implied yields from.
        //
        // UST clears rather than previewing the workbook's own Treasury
        // print. That print is persisted as UST_CURVE for drift
        // reconciliation only — USD_CMT stays the live UST source, so
        // /relval-ust-curve is what the strip shows after the save. Previewing
        // the workbook's numbers would show a vintage that never prices
        // anything and would not survive the reload.
        setUstCurve([]);
        // SOFR does preview: it is the curve the pending snapshot's
        // SOFR-linked implied yields will actually be computed from, and the
        // backend's exact match on the snapshot date returns this same curve
        // after the save. A menu with no appendix genuinely has no curve, so
        // clearing is right there too — retaining the previously loaded one
        // would preview rates that never apply, and importing an older menu
        // could even show a future curve.
        setSofrCurve(curvePointsFor(curveUploads, "SOFR_CURVE"));
        // The date the curve will be filed under is the snapshot's, not the
        // date the workbook printed the block — that is what the save sends
        // and what the read matches on.
        setSofrCurveAsOf(
          curveUploads.some((upload) => upload.source_type === "SOFR_CURVE")
            ? importedDate
            : null,
        );
        setSofrCurveFailed(false);
        clearChanges();
        showNotification(
          curveUploads.length
            ? `Imported ${importedRows.length} rows and ${curveUploads.length} ` +
              `curve${curveUploads.length === 1 ? "" : "s"} from the Asset Menu Output sheet`
            : `Imported ${importedRows.length} rows from the Asset Menu Output sheet`,
          "success",
        );
      } catch (error) {
        console.error("Error importing RelVal workbook:", error);
        showNotification(
          error?.message || "Failed to import workbook",
          "error",
        );
      } finally {
        setIsImporting(false);
      }
    },
    [
      clearChanges,
      getChangedData,
      hasPendingImport,
      pendingSourceImports,
      rowData,
      showNotification,
    ],
  );

  // Both of these are stateful (a hidden file input / a dropdown + dialog)
  // and so ride GridToolbar's customComponents slot rather than the
  // {label,onClick} customButtons array. The upload control is local rather
  // than GridToolbar's own showUpload button because core's toolbar input is
  // single-file — see SourceFileUploadButton.
  const toolbarComponents = useMemo(
    () => [
      <SourceFileUploadButton
        key="relval-source-upload"
        label="Import Source Files"
        tooltip="Import one or more source workbooks (SP spread time series, CLO Factbook, Wells RMBS, Corporate Spreads), or a single Asset Menu workbook on its own"
        acceptedFileTypes=".xlsx,.xls"
        disabled={isLoading || isSaving || isImporting}
        onUpload={handleImport}
      />,
      <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />,
    ],
    [gridConfigs, handleImport, isImporting, isLoading, isSaving],
  );

  // Export handler — mirrors the workbook `Output` sheet layout:
  // assumptions strip, UST data strip, column headers, then rows grouped by
  // Asset Class / Sub-class with section header rows.
  const handleExport = useCallback(() => {
    const headerCols = [
      "Identifier",
      "Short Name",
      "Source",
      "Date",
      "Rating",
      "Current Spread / Level / Yield",
      "Asset Leverage",
      "Friction Cost to Target",
      "Remaining C1 Permitted",
      "Base Rate",
      "Implied Yield",
      "RBC",
    ];

    const fmtSpread = (row) =>
      row?.CURRENT_SPREAD == null
        ? null
        : row.FIELD === "INDEX_OAS_TSY"
          ? (Number(row.CURRENT_SPREAD) * 100).toFixed(2)
          : Number(row.CURRENT_SPREAD).toFixed(2);
    const fmtNum = (v, dp = 4) => (v == null ? null : Number(v).toFixed(dp));
    const fmtPct = (v, dp = 2) =>
      v == null ? null : `${(Number(v) * 100).toFixed(dp)}%`;

    const aoa = [];
    aoa.push([
      `Asset Dashboard — Output${selectedDate ? ` (${selectedDate})` : ""}`,
    ]);
    aoa.push([]);

    // Assumptions strip
    if (assumptions?.length) {
      aoa.push(["Assumptions", ...assumptions.map((a) => a.label)]);
      aoa.push(["", ...assumptions.map((a) => formatAssumption(a))]);
      aoa.push([]);
    }

    // Base-rate strips — the workbook prints both curves below its rows.
    // An absent SOFR curve is recorded rather than omitted: the export is
    // what gets circulated, and a reader cannot otherwise tell that the
    // SOFR-linked implied yields came from the workbook instead of a curve.
    if (!sofrCurve?.length) {
      aoa.push([
        "SOFR",
        sofrCurveFailed
          ? "could not load — SOFR curve status unknown, implied yields may be workbook values"
          : "no curve uploaded — SOFR-linked implied yields fall back to the workbook",
      ]);
      aoa.push([]);
    }
    for (const [label, points] of [
      ["UST Data", ustCurve],
      [
        sofrCurveAsOf
          ? `SOFR (filed with the ${sofrCurveAsOf} snapshot)`
          : "SOFR",
        sofrCurve,
      ],
    ]) {
      if (!points?.length) continue;
      aoa.push([label, ...points.map((p) => p.tenor)]);
      aoa.push([
        "",
        ...points.map((p) =>
          p.yield == null ? null : `${Number(p.yield).toFixed(3)}%`,
        ),
      ]);
      aoa.push([]);
    }

    // Column headers
    aoa.push(headerCols);

    // Group rows by Asset Class -> Sub-class, preserving the order rows
    // arrive in from the backend (which already mirrors the workbook).
    const byClass = new Map();
    for (const r of rowData) {
      const cls = r.ASSET_CLASS || "(Uncategorized)";
      const sub = r.SUB_CLASS || "";
      if (!byClass.has(cls)) byClass.set(cls, new Map());
      const subs = byClass.get(cls);
      if (!subs.has(sub)) subs.set(sub, []);
      subs.get(sub).push(r);
    }

    for (const [cls, subs] of byClass) {
      aoa.push([cls]);
      for (const [sub, rows] of subs) {
        if (sub) aoa.push(["", sub]);
        for (const row of rows) {
          aoa.push([
            row.IDENTIFIER ?? "",
            row.SHORT_NAME ?? "",
            row.SOURCE ?? "",
            row.LAST_UPDATED_DATE ?? "",
            row.RATING ?? "",
            fmtSpread(row),
            fmtNum(row.ASSET_LEVERAGE, 4),
            fmtNum(row.FRICTION_COST_TO_TARGET, 2),
            fmtPct(row.REMAINING_C1_PERMITTED, 2),
            row.BASE_RATE ?? "",
            row.IMPLIED_YIELD == null
              ? null
              : `${Number(row.IMPLIED_YIELD).toFixed(4)}%`,
            fmtPct(row.RBC, 2),
          ]);
        }
      }
      aoa.push([]);
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [
      { wch: 28 }, // Identifier
      { wch: 38 }, // Short Name
      { wch: 10 }, // Source
      { wch: 12 }, // Date
      { wch: 8 }, // Rating
      { wch: 28 }, // Current Spread
      { wch: 16 }, // Asset Leverage
      { wch: 22 }, // Friction Cost
      { wch: 22 }, // Remaining C1
      { wch: 14 }, // Base Rate
      { wch: 14 }, // Implied Yield
      { wch: 10 }, // RBC
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Output");
    const fname = `RelVal_${selectedDate || "export"}.xlsx`;
    XLSX.writeFile(wb, fname);
  }, [
    assumptions,
    ustCurve,
    sofrCurve,
    sofrCurveAsOf,
    sofrCurveFailed,
    rowData,
    selectedDate,
  ]);

  // Date change handler
  const handleDateChange = useCallback((newDate) => {
    setSelectedDate(newDate);
  }, []);

  // Count of changed rows for save badge
  const changedRowCount = useMemo(() => {
    const sourceRowCount = Object.values(pendingSourceImports).reduce(
      (total, upload) => total + upload.rows.length,
      0,
    );
    if (sourceRowCount) return sourceRowCount;
    if (hasPendingImport) {
      return rowData.length;
    }
    const editedCount = Object.keys(updatedRows).length;
    const newRowCount = rowData.filter((r) => isNewRow(r)).length;
    return editedCount + newRowCount;
  }, [hasPendingImport, pendingSourceImports, updatedRows, rowData, isNewRow]);

  // The toolbar's date Select renders blank when its value is not one of
  // availableDates, so an import for a week the backend has never returned
  // has to widen the option list. Staged source dates and a pending Asset
  // Menu date are unioned in until the save round-trip refreshes the
  // authoritative list.
  // Every option is the Monday of a week. Selecting one resolves to the
  // newest data on or before it, which is that week's snapshot, so a
  // Friday-published workbook is reached through the following Monday.
  const toolbarDates = useMemo(() => {
    const weeks = new Set(availableDates);
    for (const upload of Object.values(pendingSourceImports)) {
      if (upload.asofdt) weeks.add(nextMonday(upload.asofdt));
    }
    if (pendingImportDate) weeks.add(pendingImportDate);
    return Array.from(weeks).sort().reverse();
  }, [availableDates, pendingImportDate, pendingSourceImports]);

  // Track the AG Grid displayed row count (changes as user expands/collapses
  // group pivots). Drives the dynamic grid height below.
  const [visibleRowCount, setVisibleRowCount] = useState(0);
  const handleModelUpdated = useCallback((params) => {
    setVisibleRowCount(params.api.getDisplayedRowCount());
  }, []);

  // Compact density: tighter row + header heights with smaller font (applied
  // via the sx wrappers below). Both grids share these values so they look
  // consistent. Cap the scroll viewport at 800px so the legend/footer stays
  // visible; below 800px the grid shrinks to fit visible rows so there's no
  // blank space under the last row.
  const ROW_HEIGHT = 22;
  const HEADER_HEIGHT = 30;
  const gridHeight = useMemo(() => {
    const padding = 4;
    const rows = Math.max(visibleRowCount, 1);
    const natural = HEADER_HEIGHT + rows * ROW_HEIGHT + padding;
    return `${Math.min(800, natural)}px`;
  }, [visibleRowCount]);

  return (
    <Box>
      {/* Toolbar: title, date select, save / export. Sits above the
          assumptions and UST strips so the chrome stays grouped at the top. */}
      <GridToolbar
        title="Relative Value"
        showDateSelect={true}
        availableDates={toolbarDates}
        selectedDate={hasPendingImport ? pendingImportDate || "" : selectedDate}
        onDateChange={handleDateChange}
        showUpload={false}
        showSave={canWrite}
        onSave={handleSave}
        changedRowCount={changedRowCount}
        isSaving={isSaving}
        showExport={!hasPendingImport}
        onExport={handleExport}
        exportLabel="Export Excel"
        isLoading={isLoading || isSaving || isImporting}
        customComponents={toolbarComponents}
      />
      {/* Assumptions + the two base-rate curves sit directly above the grid */}
      <AssumptionsPanel assumptions={assumptions} />
      <CurvePanel label="UST Data" points={ustCurve} />
      <CurvePanel
        label="SOFR"
        points={sofrCurve}
        // The ASOFDT the read resolved to, which is the snapshot the curve
        // was filed with — deliberately not the date the workbook printed it,
        // which is only in FILE_NAME. Worded to say exactly that: "from week
        // of X" invites reading X as the rate vintage. Shown unconditionally,
        // because which snapshot's curve is in force is the useful fact and
        // comparing it to the picker's Monday flags every current curve.
        subLabel={
          sofrCurveAsOf ? `filed with the ${sofrCurveAsOf} snapshot` : null
        }
        emptyMessage={
          isLoading
            ? null
            : sofrCurveFailed
              ? // A failed read means the curve is unknown, not that the
                // yields fell back: /relval-data loaded independently and may
                // already hold SOFR-derived yields. Matches the export.
                "Could not load — curve status unknown, implied yields may be workbook values"
              : "No curve uploaded — SOFR-linked implied yields fall back to the workbook"
        }
      />
      {/* Compact density: smaller font + tighter line-height on cells and the
          header bar so both grids fit more rows in the same vertical budget.
          Line-height matches ROW_HEIGHT so text stays vertically centered. */}
      <Box
        sx={{
          "& .ag-cell": { fontSize: "11px", lineHeight: `${ROW_HEIGHT}px` },
          "& .ag-header-cell": { fontSize: "11px" },
          "& .ag-header-cell-text": { fontSize: "11px", fontWeight: 600 },
          "& .ag-cell.relval-cell-edited": {
            backgroundColor: "rgba(255, 193, 7, 0.32) !important",
            boxShadow: "inset 0 0 0 1px rgba(245, 124, 0, 0.85)",
            fontWeight: 600,
          },
          // Red header for columns whose backend wiring is still TODO.
          "& .ag-header-cell.relval-unimplemented-header .ag-header-cell-text":
            { color: "#d32f2f" },
        }}
      >
        <PamGrid
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          groupDefaultExpanded={-1}
          groupDisplayType="multipleColumns"
          groupHideOpenParents={true}
          showToolbar={false}
          showQuickFilter={true}
          quickFilterText={quickFilterText}
          onQuickFilterChange={setQuickFilterText}
          height={gridHeight}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          getRowId={getRowId}
          updatedRows={updatedRows}
          isNewRow={isNewRow}
          isLoading={isLoading || isSaving || isImporting}
          loadingMessage={
            isImporting
              ? "Importing workbook..."
              : isSaving
                ? "Saving..."
                : "Loading data..."
          }
          onCellValueChanged={handleCellValueChanged}
          onModelUpdated={handleModelUpdated}
          onGridReady={handleGridReadyComposed}
          // Mirror every column/sort/filter gesture into the local stash so
          // unsaved layout tweaks survive a page refresh. PamGrid surfaces
          // onFilterChanged at the top level; the column events ride through
          // agGridProps as the stashBindings bundle.
          onFilterChanged={gridConfigs.onGridStateChanged}
          agGridProps={gridConfigs.stashBindings}
          showLegend={true}
          enableCellTextSelection={true}
          animateRows={true}
          undoRedoCellEditing={true}
          undoRedoCellEditingLimit={20}
        />
      </Box>
    </Box>
  );
};

export default RelValGrid;
